import { useEffect } from 'react';
import { useTranslation as useI18nextTranslation } from 'react-i18next';

import i18n from '.';
import type { Language } from './languages';
import type { LocaleNamespaces } from './namespaces';

type Namespace = keyof LocaleNamespaces;

/**
 * Registers a component's co-located translations (`locales/{en,es,ru}.json`) under its own namespace, named after
 * the component. Call it once at module level, next to the `LocaleNamespaces` augmentation that types its keys:
 *
 *     registerLocaleResources('CookBookPage', { en, es, ru });
 *     declare module '@/modules/i18n/namespaces' {
 *         interface LocaleNamespaces { CookBookPage: typeof en }
 *     }
 *
 * A language without a bundle, or a bundle missing a key of the English one, does not compile.
 */
export const registerLocaleResources = <N extends Namespace>(
    namespace: N,
    resources: Record<Language, LocaleNamespaces[N]>,
): void => {
    for (const [lang, bundle] of Object.entries(resources)) {
        if (!i18n.hasResourceBundle(lang, namespace)) {
            i18n.addResourceBundle(lang, namespace, bundle, true, true);
        }
    }
};

/**
 * Translations of a namespace registered by registerLocaleResources. Re-renders on a language switch, unlike
 * the global `i18n.t`, which is why components never import the i18next instance.
 */
export const useTranslation = <N extends Namespace>(namespace: N) => {
    const translation = useI18nextTranslation(namespace);

    // A namespace typed by the augmentation but never registered renders bare keys
    useEffect(() => {
        if (__APP_ENV__ === 'dev' && !i18n.hasResourceBundle(i18n.resolvedLanguage ?? 'en', namespace)) {
            console.warn(`[useTranslation] Namespace "${namespace}" is not registered: call registerLocaleResources('${namespace}', {...})`);
        }
    }, [namespace]);

    return translation;
};

// Site-wide translations: src/locales/<lang>/common.json
export const useCommonTranslation = () => useI18nextTranslation('common');
